import _ from "lodash";
import React, { useState } from "react";
import classes from "./TemplateAttachment.scss";
import { getThumbUrl, getGoogleFileType, getThumbnailIcon } from "Utils";
import { LockIconV2, FeedbackResponseIcon } from "SvgComponents";
import { downloadFromUrl } from "modules/Services";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import I18nHOC from "UIComponents/I18nHOC";
import LinkWithTooltip from "UIComponents/LinkWithTooltip";
import classNames from "classnames";
import ACLStore from "lib/aclStore";
import HiddenIfPreviousYear from "AppComponents/HiddenIfPreviousYear";
import {
  IconButton,
  MediaCard,
  Button,
  Dropdown,
  DropdownMenu,
  Dialog,
} from "@toddle-edu/ds-web";
import {
  DownloadCloudOutlined,
  JournalAddOutlined,
  DotsVerticalOutlined,
  DownloadOutlined,
  RefreshArrowOutlined,
  AddOutlined,
  PencilLineOutlined,
} from "@toddle-edu/ds-icons";
import { getSettingValue } from "modules/PermissionModule";
import { colors } from "@toddle-edu/ds-theme";
import { DEFAULT_LINK_THUMB_URL } from "store/static";
import {
  getViewResponseAfterSubmissionFromServiceName,
  getLtiToolDisplayName,
} from "Utils/AttachmentHelpers";
import { showNewTheatreMode } from "Utils/GetTheatreModeType";
import { Trans } from "react-i18next";
import { downloadToddleDoc } from "AppComponents/ToddleDoc/Modules/ToddleDocModule";
import { useToddleDocDownloadOptions } from "AppComponents/ToddleDoc/components/ToddleDocDownloadMenu";

const styles = {
  templateActionBtnStyle: { marginLeft: "16px" },
};

const DIALOGS_INFO = {
  RESET_SUBMISSION_TEMPLATE: {
    title: t =>
      t("common:reset_with_label", {
        label: t("classRoom:submission_template_label"),
      }),
    message: () => (
      <Trans i18nKey="classRoom:reset_submission_template_msg">
        <span>Resetting the submission template would</span>
        <span className={classes.bold}>
          delete all the changes you have made
        </span>
        <span>
          to the template. This action cannot be undone. Would you still like to
          continue?
        </span>
      </Trans>
    ),
    confirmButtonText: t =>
      t("common:reset_with_label", {
        label: t("common:template"),
      }),
    cancelButtonText: t => t("common:cancel"),
  },
};

const getText = ({ shouldShowEditResponse, userType, t, canTurnIn, isV2 }) => {
  switch (userType) {
    case "staff":
      return t("classRoom:view_template");

    case "student":
      return canTurnIn ? (
        shouldShowEditResponse ? (
          <div className={classes.templateActionBtn}>
            <PencilLineOutlined size={isV2 ? "xxx-small" : "xx-small"} />
            {isV2 ? t("common:edit") : t("classRoom:edit_response")}
          </div>
        ) : (
          <div className={classes.templateActionBtn}>
            <AddOutlined
              variant={isV2 ? "on" : "default"}
              size={isV2 ? "xxx-small" : "xx-small"}
            />
            {isV2 ? t("common:add") : t("classRoom:add_response")}
          </div>
        )
      ) : shouldShowEditResponse ? (
        t("classRoom:view_response")
      ) : (
        t("classRoom:view_template")
      );

    case "parent":
      return shouldShowEditResponse
        ? t("classRoom:view_response")
        : t("classRoom:view_template");
  }
};

const TemplateActionButton = React.memo(props => {
  const {
    showTemplateActionButton,
    onUseTemplateClick,
    canTurnIn,
    userType,
    shouldShowEditResponse,
    t,
    attachment,
    buttonVariant,
    buttonSize,
    showAddToJournal,
    onClickCreatePost,
    disableTemplateButton,
    isV2 = false,
  } = props;

  const dsVersion = isV2 ? "2.0" : "1.0";

  return (
    <div className={classes.templateButtonsContainer}>
      {showAddToJournal && shouldShowEditResponse && !isV2 ? (
        <IconButton
          dsVersion={dsVersion}
          onClick={onClickCreatePost}
          icon={<JournalAddOutlined />}
          _legacy_variant={"plain"}
          _legacy_size={"medium"}
          variant={"neutral"}
          type={"plain"}
        />
      ) : null}
      {showTemplateActionButton ? (
        <HiddenIfPreviousYear>
          <div
            className={classes.templateActionButton}
            style={styles.templateActionBtnStyle}
          >
            <Button
              _legacy_size={"medium"}
              _legacy_variant={"outlined"}
              variant={!shouldShowEditResponse ? "progressive" : "neutral"}
              type={!shouldShowEditResponse ? "fill" : "outlined"}
              onClick={onUseTemplateClick}
              disabled={disableTemplateButton}
              dsVersion={dsVersion}
              size={isV2 ? "small" : "medium"}
            >
              {getText({
                shouldShowEditResponse,
                userType,
                t,
                canTurnIn,
                isV2,
              })}
            </Button>
          </div>
        </HiddenIfPreviousYear>
      ) : _.get(attachment, "type", "") === "WORKBOOK" &&
        !showNewTheatreMode() ? (
        <HiddenIfPreviousYear>
          <div
            className={classes.templateActionButton}
            style={styles.templateActionBtnStyle}
          >
            <Button
              _legacy_size={buttonSize}
              _legacy_variant={buttonVariant}
              variant={!shouldShowEditResponse ? "progressive" : "neutral"}
              type={!shouldShowEditResponse ? "fill" : "outlined"}
              onClick={onUseTemplateClick}
              disabled={false}
              dsVersion={dsVersion}
            >
              {getText({
                shouldShowEditResponse,
                userType,
                t,
                canTurnIn,
                isV2,
              })}
            </Button>
          </div>
        </HiddenIfPreviousYear>
      ) : null}
    </div>
  );
});

TemplateActionButton.displayName = "TemplateActionButton";

const TemplateAttachment = ({
  attachment,
  onUseTemplateClick,
  downloadFromUrl,
  t,
  canTurnIn,
  shouldShowTemplateToParent,
  userType,
  shouldShowEditResponse,
  wrapperStyle,
  showMediaCard,
  attachmentBottomComponent,
  onClickCreatePost,
  showAddToJournal,
  isFeedbackGiven,
  submittedTemplate,
  resetStudentTemplate,
  isSubmitted,
  isScheduled,
  isArchivedClass,
  subtext,
  onMediaClick,
  isSubmissionLockedForStudent,
  isSubmissionStateLocked,
  isV2 = false,
  isArchivedSubmission = false,
  assignmentState,
  downloadToddleDoc,
  ltiIntegrationCreds = [],
}) => {
  const [currentDialogAction, setCurrentDialogAction] = useState("");

  // get updated attachment from submittedTemplate when student has added response
  const submittedTemplateAttachment = _.first(
    _.get(submittedTemplate, "attachments", [])
  );

  const isToddleDocDownloadWithCommentsEnabled = ACLStore.can(
    "FeatureFlag:EnableDownloadToddleDocWithComments"
  );
  const isTemplateToddleDoc =
    _.get(attachment, "type") === "TODDLE_DOC" &&
    isToddleDocDownloadWithCommentsEnabled;
  const isResponseToddleDoc =
    _.get(submittedTemplateAttachment, "type") === "TODDLE_DOC" &&
    isToddleDocDownloadWithCommentsEnabled;

  const templateDownload = useToddleDocDownloadOptions({
    attachment,
    optionKeyPrefix: "templateDownload",
    testIdPrefix: "templateAttachment-templateDownload",
  });
  const responseDownload = useToddleDocDownloadOptions({
    attachment: submittedTemplateAttachment,
    optionKeyPrefix: "responseDownload",
    testIdPrefix: "templateAttachment-responseDownload",
  });
  const templateAttachment = !_.isEmpty(submittedTemplateAttachment)
    ? submittedTemplateAttachment
    : attachment;

  const { thumbUrl, color, isIcon } = getThumbUrl({
    attachment: templateAttachment,
  });
  const isPermission = !!_.get(attachment, "metadata.permission");
  const isLinkType = _.get(attachment, "type", "") === "LINK";
  const isWorkbookType = _.get(attachment, "type", "") === "WORKBOOK";
  const workbookPageCount =
    isWorkbookType && _.get(attachment, "metadata.numOfPages", 0);

  const workbookPageCountLabel = workbookPageCount
    ? t("common:workbook_page", {
        count: workbookPageCount,
      })
    : "";
  const isToddleDoc = _.get(attachment, "type") === "TODDLE_DOC";
  let fileType = isToddleDoc ? "TODDLE-DOC" : _.get(attachment, "type");
  if (isLinkType) {
    fileType = getGoogleFileType(_.get(attachment, "mimeType", "")) || fileType;
  }
  const isParent = userType === "parent";

  const readOnlyMode = isParent && isLinkType && !shouldShowTemplateToParent;

  const containerClass = classNames(
    { [classes.container]: true },
    { [classes.disabledContainer]: readOnlyMode }
  );
  const isLTITool = _.isEqual(
    _.get(templateAttachment, "metadata.type"),
    "LTI_TOOL"
  );

  const serviceName = _.get(templateAttachment, "metadata.service");

  const viewResponseAfterSubmission = getViewResponseAfterSubmissionFromServiceName(
    {
      ltiIntegrationCreds,
      serviceName,
    }
  );

  const isLTIAttempted = _.get(
    templateAttachment,
    "metadata.isLTIAttempted",
    false
  );

  const subtitleLabel = isLTITool
    ? getLtiToolDisplayName(templateAttachment)
    : isToddleDoc
    ? undefined
    : subtext;

  const shouldUseWorkbook = ACLStore.can("FeatureFlag:Workbook");

  const showTemplateActionButton =
    ((isPermission || shouldUseWorkbook) &&
      (!isParent ||
        shouldShowTemplateToParent ||
        _.get(attachment, "type", "") === "IMAGE") &&
      (!showNewTheatreMode() ||
        (showNewTheatreMode() && canTurnIn && !isParent)) &&
      (!isLTITool || !isLTIAttempted)) ||
    (!isParent && isLTITool && assignmentState === "PUBLISHED") ||
    (isLTITool &&
      isLTIAttempted &&
      userType === "student" &&
      viewResponseAfterSubmission);

  const serviceType = _.get(attachment, "metadata.serviceType");
  const isFromGoogleDriveOrOneDrive =
    _.includes(["GOOGLE", "MICROSOFT"], serviceType) && isLinkType;

  const showSimilarityIndex = userType === "student" && shouldShowEditResponse;

  const disableTemplateButton =
    (isFromGoogleDriveOrOneDrive &&
      (isScheduled || (isSubmitted && _.isEmpty(submittedTemplate)))) ||
    isArchivedClass;

  const notAttemptedAndLocked =
    !isLTIAttempted && (isParent || !isSubmissionStateLocked);
  const publishedForNonParent = !isParent && assignmentState === "PUBLISHED";
  const studentAlreadyAttempted = userType === "student" && isLTIAttempted;
  const disableLTICardClick =
    isLTITool &&
    (notAttemptedAndLocked || publishedForNonParent || studentAlreadyAttempted);

  const disableTemplateMediaCardClick =
    (isFromGoogleDriveOrOneDrive &&
      (isScheduled || _.isEmpty(submittedTemplate))) ||
    isArchivedClass ||
    disableLTICardClick;

  const enableTemplateCardClickResponse =
    _.isEqual(userType, "student") &&
    ACLStore.can("FeatureFlag:EnableTemplateResponseCardClick") &&
    showTemplateActionButton &&
    !disableTemplateButton;

  const templateActionOptions = isTemplateToddleDoc
    ? [...templateDownload.options]
    : [
        {
          key: "downloadTemplate",
          label: t("common:download_with_label", {
            label: t("common:template"),
          }),
          icon: <DownloadOutlined size="xx-small" />,
        },
      ];

  const dsVersion = isV2 ? "2.0" : "1.0";

  if (submittedTemplateAttachment) {
    if (isResponseToddleDoc) {
      // For ToddleDoc responses, expose the with/without-comments split.
      _.forEach(responseDownload.options, option => {
        templateActionOptions.push(option);
      });
    } else {
      // Allow downloading response if student has submitted response
      templateActionOptions.push({
        key: "downloadResponse",
        label: t("common:download_with_label", {
          label: t("common:response"),
        }),
        icon: <DownloadOutlined size="xx-small" />,
      });
    }

    const shouldShowReset = !(
      isSubmitted ||
      isParent ||
      isSubmissionLockedForStudent ||
      isArchivedSubmission
    );

    // Allow resetting template if student has submitted response
    if (shouldShowReset) {
      templateActionOptions.push({
        key: "resetTemplate",
        label: t("common:reset_with_label", {
          label: t("common:template"),
        }),
        icon: <RefreshArrowOutlined size="xx-small" />,
      });
    }
  }

  if (isV2 && showAddToJournal && shouldShowEditResponse) {
    templateActionOptions.push({
      key: "add_to_portfolio",
      label: t("journal:add_to_portfolio"),
      icon: <JournalAddOutlined size="xx-small" />,
    });
  }

  const onCardActionClick = action => {
    if (isTemplateToddleDoc && templateDownload.isOwnOptionKey(action)) {
      downloadToddleDoc({
        attachment,
        shouldAskForSave: true,
        includeComments: templateDownload.getIncludeCommentsFromKey(action),
      });
      return;
    }
    if (isResponseToddleDoc && responseDownload.isOwnOptionKey(action)) {
      downloadToddleDoc({
        attachment: submittedTemplateAttachment,
        shouldAskForSave: true,
        includeComments: responseDownload.getIncludeCommentsFromKey(action),
      });
      return;
    }
    switch (action) {
      case "downloadTemplate":
        if (_.isEqual(attachment?.type, "TODDLE_DOC")) {
          downloadToddleDoc({
            attachment,
            shouldAskForSave: true,
          });
          break;
        }
        downloadFromUrl({
          attachment,
          shouldAskForSave: true,
        });
        break;
      case "downloadResponse":
        if (_.isEqual(attachment?.type, "TODDLE_DOC")) {
          downloadToddleDoc({
            attachment: submittedTemplateAttachment,
            shouldAskForSave: true,
          });
          break;
        }
        downloadFromUrl({
          attachment: submittedTemplateAttachment,
          shouldAskForSave: true,
        });
        break;
      case "resetTemplate":
        setCurrentDialogAction("RESET_SUBMISSION_TEMPLATE");
        break;
      case "add_to_portfolio":
        onClickCreatePost();
        break;
    }
  };

  const templateActions = () => {
    const onDropDownMenuClick = event => {
      event.domEvent.stopPropagation();
      const action = event.key;
      onCardActionClick(action);
    };

    const stopPropagationHandler = event => {
      event.stopPropagation();
    };

    return (
      <div
        onClick={stopPropagationHandler}
        className={classes.templateActionsContainer}
      >
        <TemplateActionButton
          showTemplateActionButton={showTemplateActionButton}
          onUseTemplateClick={onUseTemplateClick}
          canTurnIn={canTurnIn}
          userType={userType}
          shouldShowEditResponse={shouldShowEditResponse}
          t={t}
          attachment={attachment}
          buttonVariant={"outlined"}
          buttonSize={isV2 ? "small" : "medium"}
          showAddToJournal={showAddToJournal && !isLTITool}
          onClickCreatePost={onClickCreatePost}
          disableTemplateButton={disableTemplateButton}
          isV2={isV2}
        />
        {!isLTITool && !_.isEmpty(templateActionOptions) ? (
          <Dropdown
            getPopupContainer={el => {
              return el;
            }}
            disabled={isLinkType}
            onVisibleChange={visible => {
              if (isTemplateToddleDoc) {
                templateDownload.handleMenuVisibleChange(visible);
              }
              if (isResponseToddleDoc) {
                responseDownload.handleMenuVisibleChange(visible);
              }
            }}
            overlay={
              <DropdownMenu
                onClick={onDropDownMenuClick}
                options={templateActionOptions}
              />
            }
            placement={"bottomRight"}
          >
            <IconButton
              onClick={stopPropagationHandler}
              icon={
                <DotsVerticalOutlined variant={"subtle"} size={"xx-small"} />
              }
              _legacy_variant={"plain"}
              variant={"neutral"}
              type={"plain"}
              dsVersion={dsVersion}
            />
          </Dropdown>
        ) : null}
      </div>
    );
  };

  const isDefaultLinkThumbUrl =
    _.isEqual(fileType, "LINK") &&
    _.isEqual(_.get(attachment, "thumbUrl"), DEFAULT_LINK_THUMB_URL);

  const mimeType = _.get(attachment, "mimeType");

  const thumbnailIcon = isIcon
    ? getThumbnailIcon({ type: fileType, mimeType })
    : undefined;
  const onMediaCardClick = enableTemplateCardClickResponse
    ? onUseTemplateClick
    : disableTemplateMediaCardClick
    ? undefined
    : onMediaClick;

  const renderComponent = () => {
    return showMediaCard ? (
      <MediaCard
        cardType={"medium"}
        name={_.get(attachment, "name")}
        type={fileType}
        icon={isToddleDoc ? undefined : thumbnailIcon}
        mimeType={mimeType}
        thumbUrl={
          isIcon || isDefaultLinkThumbUrl || isToddleDoc ? undefined : thumbUrl
        }
        pageCount={workbookPageCountLabel}
        bottomElement={
          showSimilarityIndex ? attachmentBottomComponent?.() : undefined
        }
        options={templateActions()}
        subtext={subtitleLabel}
        onClick={onMediaCardClick}
        dsVersion={dsVersion}
        url={""}
      />
    ) : (
      <div className={classes.wrapper} style={wrapperStyle}>
        {!!isFeedbackGiven && (
          <span className={classes.notificationCounter}>
            <FeedbackResponseIcon width={16} height={16} fill={colors.white} />
          </span>
        )}
        <div
          className={classes.attachmentIconContainer}
          style={{
            backgroundImage: `url(${thumbUrl})`,
            backgroundColor: color,
            backgroundSize: isIcon ? "35%" : "cover",
          }}
        ></div>
        <div className={classes.attachmentInfo}>
          <div className={classes.attachmentName}>{attachment.name}</div>
          <div className={classes.attachmentType}>
            {isWorkbookType
              ? t("common:workbook_page", {
                  count: _.get(attachment, "metadata.numOfPages", 1),
                })
              : fileType}
          </div>
        </div>

        {showAddToJournal && shouldShowEditResponse && (
          <IconButton
            onClick={onClickCreatePost}
            icon={<JournalAddOutlined />}
            _legacy_variant={"plain"}
            _legacy_size={"medium"}
            variant={"neutral"}
            type={"plain"}
            dsVersion={dsVersion}
          />
        )}

        {/* Don't show download icon for like or workbook or worksheet type attachments */}
        {!isLinkType && !isWorkbookType && (
          <IconButton
            onClick={e => {
              e.stopPropagation();
              downloadFromUrl({
                attachment: attachment,
                shouldAskForSave: true,
              });
            }}
            icon={<DownloadCloudOutlined />}
            _legacy_variant={"plain"}
            _legacy_size={"medium"}
            variant={"neutral"}
            type={"plain"}
            dsVersion={dsVersion}
          />
        )}
        {readOnlyMode && <LockIconV2 />}
        <TemplateActionButton
          showTemplateActionButton={showTemplateActionButton}
          onUseTemplateClick={onUseTemplateClick}
          canTurnIn={canTurnIn}
          userType={userType}
          shouldShowEditResponse={shouldShowEditResponse}
          t={t}
          attachment={attachment}
          buttonVariant={"outlined-subtle"}
          buttonSize={"large"}
          disableTemplateButton={disableTemplateButton}
        />
      </div>
    );
  };

  const onDialogConfirmClick = () => {
    switch (currentDialogAction) {
      case "RESET_SUBMISSION_TEMPLATE":
        resetStudentTemplate({ submittedTemplate });
        break;
    }

    setCurrentDialogAction("");
  };

  return (
    <div className={containerClass}>
      {readOnlyMode ? (
        <LinkWithTooltip
          tooltip={t("toastMsgs:document_sharing_permission")}
          placement={"topLeft"}
          href={null}
        >
          {renderComponent()}
        </LinkWithTooltip>
      ) : (
        renderComponent()
      )}

      <Dialog
        isOpen={!_.isEmpty(currentDialogAction)}
        variant="delete-dialog"
        title={DIALOGS_INFO?.[currentDialogAction]?.title?.(t)}
        onClose={() => setCurrentDialogAction("")}
        message={DIALOGS_INFO?.[currentDialogAction]?.message?.()}
        buttonOneText={DIALOGS_INFO?.[currentDialogAction]?.cancelButtonText?.(
          t
        )}
        buttonTwoText={DIALOGS_INFO?.[currentDialogAction]?.confirmButtonText?.(
          t
        )}
        onButtonOneClick={() => setCurrentDialogAction("")}
        onButtonTwoClick={onDialogConfirmClick}
      ></Dialog>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userType = _.get(state, "login.userInfo.user_type");
  let shouldShowTemplateToParent;
  if (userType === "parent") {
    shouldShowTemplateToParent = getSettingValue({
      name: "UnlockFamilyStudentTemplate",
      courseId: ownProps.assignmentCourseId,
      organizationId: _.get(state, "login.userInfo.org_id"),
      userEntityType: _.get(state, "login.userInfo.userEntityType"),
    });
  }
  return {
    shouldShowTemplateToParent,
  };
};

const mapActionCreators = { downloadFromUrl, downloadToddleDoc };

export default compose(
  connect(mapStateToProps, mapActionCreators),
  I18nHOC({ resource: ["common", "classRoom", "toastMsgs"] })
)(TemplateAttachment);
